/*
 * TaskQueue.tsx — S57: Processing Queue Monitor
 * API: GET /api/v1/tasks/queue-stats, GET /api/v1/tasks/failed
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch } from '../constants';
import { realtimeService } from '../services/realtimeService';

interface QueueStats {
  pending: number;
  processing: number;
  completed_last_hour: number;
  failed_last_hour: number;
  retry_queue: number;
  avg_processing_time_ms: number;
  worker_running: boolean;
}

interface FailedTask {
  id: number;
  task_type: string;
  resource_id: string;
  error_message: string;
  retry_count: number;
  created_at: string;
}

export default function TaskQueue() {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [failed, setFailed] = useState<FailedTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/tasks/queue-stats`, null),
      safeFetch(`${API_V1}/tasks/failed`, { failed_tasks: [] }),
    ])
      .then(([s, f]) => {
        setStats(s);
        setFailed(Array.isArray(f) ? f : f?.failed_tasks || f?.tasks || []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  // SSE replaces 15s poll — refresh on agent_update events
  useEffect(() => {
    load();
    const unsub = realtimeService.on('agent_update', () => { load(); });
    return () => { unsub(); };
  }, []);

  if (loading && !stats) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading queue stats...</p></div>;
  if (error && !stats) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );
  if (!stats) return null;

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Processing Queue</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: stats.worker_running ? 'var(--green)' : 'var(--red)',
            display: 'inline-block',
          }} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Worker {stats.worker_running ? 'Running' : 'Stopped'}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Pending', value: stats.pending, color: 'var(--amber)' },
          { label: 'Processing', value: stats.processing, color: 'var(--blue)' },
          { label: 'Completed/hr', value: stats.completed_last_hour, color: 'var(--green)' },
          { label: 'Failed/hr', value: stats.failed_last_hour, color: 'var(--red)' },
          { label: 'Avg Time', value: stats.avg_processing_time_ms ? `${(stats.avg_processing_time_ms / 1000).toFixed(1)}s` : '\u2014', color: 'var(--text-primary)' },
        ].map((m, i) => (
          <div key={i} className="card" style={{ padding: 12, textAlign: 'center' }}>
            <div className="stitle" style={{ marginBottom: 2, fontSize: 10 }}>{m.label}</div>
            <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      {failed.length > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <h3 className="stitle" style={{ marginBottom: 12, color: 'var(--red)' }}>Failed Tasks</h3>
          {failed.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{t.task_type}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--text-tertiary)', marginLeft: 8 }}>#{t.resource_id}</span>
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 2 }}>{t.error_message?.slice(0, 80)}</div>
              </div>
              <span className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>retry {t.retry_count}</span>
            </div>
          ))}
        </div>
      )}

      {failed.length === 0 && (
        <div className="card" style={{ padding: 20, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>No failed tasks. Queue is healthy.</p>
        </div>
      )}
    </div>
  );
}
